import { EstadoEntidade } from "@/shared/enumeradores";
import { Paginacao } from "@/shared/interface";
import axios from "./axios-handler.service";

const BASE_URL = "/api/CentralNotificacoes";

export interface TipoNotificacao {
  nome: string;
  cor: string;
  idTipoNotificacao: number;
}

export enum TipoEnvioNotificacao {
  Nenhum = 0,
  Todos = 1,
  Ativos = 2,
}

export enum PublicoEnvioNotificacao {
  Gratis = 0,
  Lite = 1,
  Essencial = 2,
  EssencialConectada = 3,
  SuperConectada = 4,
  HiperConectada = 5,
}

export enum StatusNotificacao {
  Novo = 1,
  Agendado = 2,
  Enviando = 3,
  Enviado = 4,
  Erro = 5,
}

export interface Notificacao {
  idNotificacao: string;
  tipoNotificacao: TipoNotificacao;
  estadoEntidade: EstadoEntidade;
  data: string;
  titulo: string;
  descricao: string;
  enviadoPara: TipoEnvioNotificacao;
  status: StatusNotificacao;
  quantidadeLido: number;
  quantidadeAmei: number;
  quantidadeGostei: number;
  quantidadeNaoGostei: number;
  quantidadeComentaram: number;
  imagem: string | null;
  imagemCompleta: string | null;
  publico: PublicoEnvioNotificacao;
  descricaoCatalogos: string;
}

export interface Comentario {
  idComentario: string;
  idNotificacao: string;
  idConsultor: number;
  consultor: {
    idConsultor: number;
    nome: string;
  };
  notificacao: {
    titulo: string;
  };
  comentario: string;
  resposta?: string;
  dataResposta?: string;
  dataLeituraBackoffice?: string;
  dataCriacao: string;
  reacao?: number;
  lido: boolean;
}

export interface Consultor {
  idConsultor: number;
  nome: string;
}

export interface RespostaComentario {
  idNotificacao: string;
  idConsultor: number;
  resposta: string;
}

export interface MarcarLidoPayload {
  idNotificacao: string;
  idConsultor: number;
  lido: boolean;
}

export interface FiltroCentralNotificacoes {
  idTipo: number | null;
  tipoEnvio: TipoEnvioNotificacao | null;
  term: string;
  offset: number;
  limit: number;
  order?: string;
}

export interface FiltroComentariosNotificacao {
  idConsultor?: number | null;
  txtPesquisa: string;
  idTipoNotificacao?: number | null;
  dataComentario?: string | null;
  respondido?: boolean | null;
  offset: number;
  limit: number;
  order?: string;
  idNotificacao?: string;
}
export interface ComentarioDTO {
  idNotificacao: string;
  idConsultor: number;
  reacao: number | null;
  comentario: string;
  resposta: string | null;
  dataResposta: Date | null;
  dataCriacao: Date;
  dataLeituraBackoffice: Date | null;
  dataLeituraResposta: Date | null;
  dataAlteracao: Date | null;
  nomeConsultor: string;
  tituloNotificacao: string;
}

export class CentralNotificacoesService {
  static async listarNotificacoes(
    filtro: FiltroCentralNotificacoes
  ): Promise<Notificacao[]> {
    const { idTipo, tipoEnvio, term, offset, limit, order } = filtro;
    const response = await axios.get<Notificacao[]>(
      `${BASE_URL}/ListarNotificacao`,
      {
        params: {
          txtPesquisa: term,
          idTipoNotificacao: idTipo,
          enviadoPara: tipoEnvio,
          offset,
          limit,
          order,
        },
      }
    );
    return response.data;
  }

  static async listarComentarios(
    filtro: FiltroComentariosNotificacao
  ): Promise<Paginacao<ComentarioDTO>> {
    const {
      idConsultor,
      txtPesquisa,
      idTipoNotificacao,
      dataComentario,
      respondido,
      offset,
      limit,
      order,
      idNotificacao,
    } = filtro;

    const response = await axios.get<Paginacao<ComentarioDTO>>(
      `${BASE_URL}/ListarComentarios`,
      {
        params: {
          idConsultor,
          txtPesquisa: txtPesquisa,
          idTipoNotificacao: idTipoNotificacao,
          dataComentario,
          respondido,
          offset,
          limit,
          order,
          idNotificacao,
        },
      }
    );

    return response.data;
  }

  static async listarTiposNotificacao(): Promise<TipoNotificacao[]> {
    const response = await axios.get<TipoNotificacao[]>(
      `${BASE_URL}/ListarTiposNotificacao`
    );
    return response.data;
  }

  static listarTipoEnvioNotificacao() {
    const dicionario = Object.keys(TipoEnvioNotificacao);
    return dicionario
      .slice(0, dicionario.length / 2)
      .map((x) => ({ value: +x, text: TipoEnvioNotificacao[+x] }));
  }

  static async enviarNotificacao(
    idNotificacao: string,
    enviarPara: TipoEnvioNotificacao | null,
    publico: PublicoEnvioNotificacao | null,
    idsCatalogos: string[]
  ) {
    await axios.post(`${BASE_URL}/EnviarNotificacao`, {
      idNotificacao,
      enviarPara,
      publico,
      idsCatalogos,
    });
  }

  static async excluirNotificacao(idNotificacao: string): Promise<void> {
    await axios.post(`${BASE_URL}/ExcluirNotificacao`, { idNotificacao });
  }

  static async manterNotificacao(
    notificacao: Notificacao,
    arquivo: File | null,
    excluirImagem = false
  ): Promise<void> {
    const formData = new FormData();

    formData.append("notificacao", JSON.stringify(notificacao));
    formData.append("excluirImagem", excluirImagem.toString())

    if (arquivo) {
      formData.append("file", arquivo);
    }

    await axios.post(BASE_URL + "/Manter", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  static async obterNotificacao(id: string) {
    const res = await axios.get<Notificacao>(`${BASE_URL}/Obter`, {
      params: { id },
    });
    return res.data;
  }

  static async responderComentario(payload: RespostaComentario) {
    return axios.post(`${BASE_URL}/ResponderComentario`, payload);
  }

  static async marcarLido(payload: MarcarLidoPayload) {
    return axios.post(`${BASE_URL}/MarcarLido`, payload);
  }

  static async contarComentariosNaoLidos(): Promise<number> {
    const response = await axios.get<number>(
      `${BASE_URL}/ContarComentariosNaoLidos`
    );
    return response.data;
  }
}
